import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, RadioGroup } from '@asset-metrix/portal-ui';

import type { DashboardSharingStatus } from './utils/dashboard.types';
import { DASHBOARD_SHARING_OPTIONS } from './utils/dashboardSharing';

import styles from './dashboard-modals.module.scss';

type ShareDashboardModalProps = {
  opened: boolean;
  sharingStatus: DashboardSharingStatus;
  loading?: boolean;
  onClose: () => void;
  onSave: (sharingStatus: DashboardSharingStatus) => void;
};

function isDashboardSharingStatus(
  value: string
): value is DashboardSharingStatus {
  return value === 'private' || value === 'global';
}

export function ShareDashboardModal({
  opened,
  sharingStatus,
  loading,
  onClose,
  onSave,
}: ShareDashboardModalProps) {
  const { t } = useTranslation();
  const [selected, setSelected] =
    useState<DashboardSharingStatus>(sharingStatus);

  useEffect(() => {
    if (opened && !loading) {
      setSelected(sharingStatus);
    }
  }, [opened, sharingStatus, loading]);

  const applySharingStatus = (value: DashboardSharingStatus) => {
    if (loading) {
      return;
    }

    if (value === sharingStatus) {
      onClose();
      return;
    }

    setSelected(value);
    onSave(value);
    onClose();
  };

  return (
    <Modal
      data-testid="share-dashboard-modal"
      modalType="info"
      opened={opened}
      onClose={onClose}
      size={560}
      title={t('app.dashboard.shareModal.title')}
    >
      <div className={styles.shareModalBody}>
        <p className={styles.shareModalInstruction}>
          {t('app.dashboard.shareModal.instruction')}
        </p>
        <RadioGroup
          aria-label={t('app.dashboard.shareModal.instruction')}
          disableRadioGroup={loading}
          direction="row"
          name="dashboard-sharing"
          onChange={(value) => {
            if (isDashboardSharingStatus(value)) {
              applySharingStatus(value);
            }
          }}
          options={DASHBOARD_SHARING_OPTIONS.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
            description: t(option.descriptionKey),
            iconName: option.iconName,
          }))}
          value={selected}
          variant="cards"
        />
      </div>
    </Modal>
  );
}
